'use client';

import {
  BookOpen,
  Briefcase,
  Code2,
  Lightbulb,
  type LucideIcon,
  MessageCircle,
} from 'lucide-react';
import { useTranslations } from 'next-intl';
import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { cn } from '@/lib/utils';
import { usePromptBuilderStore } from './promptBuilderStore';

/** Translator bound to the `research` namespace, threaded into the module-level
 *  fallback-template builder (not a React component; cannot call the hook). */
type TFn = ReturnType<typeof useTranslations>;

/** Static fallback templates used when the server has not yet returned templates. */
interface LocalTemplate {
  id: string;
  name: string;
  category: string;
  description: string;
  icon: LucideIcon;
}

function fallbackTemplates(t: TFn): LocalTemplate[] {
  return [
    {
      id: 'academic',
      name: t('conversationTemplates.academicName'),
      category: 'academic',
      description: t('conversationTemplates.academicDesc'),
      icon: BookOpen,
    },
    {
      id: 'business',
      name: t('conversationTemplates.businessName'),
      category: 'business',
      description: t('conversationTemplates.businessDesc'),
      icon: Briefcase,
    },
    {
      id: 'technical',
      name: t('conversationTemplates.technicalName'),
      category: 'technical',
      description: t('conversationTemplates.technicalDesc'),
      icon: Code2,
    },
    {
      id: 'creative',
      name: t('conversationTemplates.creativeName'),
      category: 'creative',
      description: t('conversationTemplates.creativeDesc'),
      icon: Lightbulb,
    },
    {
      id: 'general',
      name: t('conversationTemplates.generalName'),
      category: 'general',
      description: t('conversationTemplates.generalDesc'),
      icon: MessageCircle,
    },
  ];
}

/** Map category strings from server templates to icons. */
const CATEGORY_ICONS: Record<string, LucideIcon> = {
  academic: BookOpen,
  business: Briefcase,
  technical: Code2,
  creative: Lightbulb,
  general: MessageCircle,
};

interface ConversationTemplatesProps {
  selectedTemplateId: string | null;
  onSelectTemplate: (templateId: string) => void;
}

function ConversationTemplates({
  selectedTemplateId,
  onSelectTemplate,
}: ConversationTemplatesProps) {
  const t = useTranslations('research');
  const serverTemplates = usePromptBuilderStore((s) => s.templates);

  // Request templates from the server on mount
  React.useEffect(() => {
    usePromptBuilderStore.getState().actions.loadTemplates();
  }, []);

  // Use server templates when available, otherwise fall back to local definitions
  const templates: LocalTemplate[] = React.useMemo(() => {
    if (serverTemplates.length > 0) {
      return serverTemplates.map((t) => ({
        id: t.id,
        name: t.name,
        category: t.category,
        description: t.content,
        icon: CATEGORY_ICONS[t.category] ?? MessageCircle,
      }));
    }
    return fallbackTemplates(t);
  }, [serverTemplates, t]);

  return (
    <div className="flex flex-col gap-3">
      <h2 className="font-serif text-base font-medium text-foreground">{t('conversationTemplates.heading')}</h2>
      <p className="text-sm text-muted-foreground">
        {t('conversationTemplates.subheading')}
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1">
        {templates.map((tpl) => {
          const Icon = tpl.icon;
          const isSelected = selectedTemplateId === tpl.id;

          return (
            <button
              key={tpl.id}
              type="button"
              onClick={() => onSelectTemplate(tpl.id)}
              className="text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
            >
              <Card
                className={cn(
                  'cursor-pointer transition-colors duration-150 hover:border-accent/40',
                  isSelected && 'border-accent bg-accent/5',
                )}
              >
                <CardHeader className="flex-row items-center gap-3 p-4">
                  <div
                    className={cn(
                      'flex h-9 w-9 shrink-0 items-center justify-center rounded-md',
                      isSelected ? 'bg-accent/15 text-accent' : 'bg-muted text-muted-foreground',
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </div>
                  <CardTitle className="text-sm">{tpl.name}</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4 pt-0">
                  <p className="text-xs leading-relaxed text-muted-foreground">{tpl.description}</p>
                </CardContent>
              </Card>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export { ConversationTemplates };
